import React, { use, useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Container,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  Paper,
  useTheme,
  useMediaQuery,
  Skeleton,
  LinearProgress,
  Autocomplete,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TextField } from "@mui/material";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const Statistics = () => {
  const { accessToken } = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();
  const isXsScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const location = useLocation();
  const { siteId, groupId, deviceId } = location.state || {};

  // State
  const [sites, setSites] = useState([]);
  const [groups, setGroups] = useState([]);
  const [devices, setDevices] = useState([]);
  const [deviceDetails, setDeviceDetails] = useState(null);
  const [measurements, setMeasurements] = useState({
    site: [],
    group: [],
    device: [],
    comparison: [],
  });
  const [selectedSite, setSelectedSite] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");
  const [selectedDevice, setSelectedDevice] = useState("");
  const [timeRange, setTimeRange] = useState("7d");
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // States for Comparison feature
  const [compareMode, setCompareMode] = useState(false);
  const [compareByGroup, setCompareByGroup] = useState(false);
  const [selectedForComparison, setSelectedForComparison] = useState([]);
  const [isComparisonLoading, setIsComparisonLoading] = useState(false);

  const baseURL = process.env.REACT_APP_BASEURL;

  // Metric configurations
  const metricColors = {
    moisture: [
      {
        backgroundColor: "rgba(25, 118, 210, 1)",
        borderColor: "rgba(25, 118, 210, 1)",
        pointBackgroundColor: "rgba(25, 118, 210, 1)",
      },
      {
        backgroundColor: "rgba(40, 167, 69, 1)",
        borderColor: "rgba(40, 167, 69, 1)",
        pointBackgroundColor: "rgba(40, 167, 69, 1)",
      },
      {
        backgroundColor: "rgba(220, 53, 69, 1)",
        borderColor: "rgba(220, 53, 69, 1)",
        pointBackgroundColor: "rgba(220, 53, 69, 1)",
      },
      {
        backgroundColor: "rgba(255, 209, 102, 1)",
        borderColor: "rgba(255, 209, 102, 1)",
        pointBackgroundColor: "rgba(255, 209, 102, 1)",
      },
    ],
    temperature: [
      {
        backgroundColor: "rgba(25, 118, 210, 1)",
        borderColor: "rgba(25, 118, 210, 1)",
        pointBackgroundColor: "rgba(25, 118, 210, 1)",
      },
      {
        backgroundColor: "rgba(40, 167, 69, 1)",
        borderColor: "rgba(40, 167, 69, 1)",
        pointBackgroundColor: "rgba(40, 167, 69, 1)",
      },
      {
        backgroundColor: "rgba(220, 53, 69, 1)",
        borderColor: "rgba(220, 53, 69, 1)",
        pointBackgroundColor: "rgba(220, 53, 69, 1)",
      },
      {
        backgroundColor: "rgba(255, 209, 102, 1)",
        borderColor: "rgba(255, 209, 102, 1)",
        pointBackgroundColor: "rgba(255, 209, 102, 1)",
      },
    ],
    salinity: [
      {
        backgroundColor: "rgba(25, 118, 210, 1)",
        borderColor: "rgba(25, 118, 210, 1)",
        pointBackgroundColor: "rgba(25, 118, 210, 1)",
      },
      {
        backgroundColor: "rgba(40, 167, 69, 1)",
        borderColor: "rgba(40, 167, 69, 1)",
        pointBackgroundColor: "rgba(40, 167, 69, 1)",
      },
      {
        backgroundColor: "rgba(220, 53, 69, 1)",
        borderColor: "rgba(220, 53, 69, 1)",
        pointBackgroundColor: "rgba(220, 53, 69, 1)",
      },
      {
        backgroundColor: "rgba(255, 209, 102, 1)",
        borderColor: "rgba(255, 209, 102, 1)",
        pointBackgroundColor: "rgba(255, 209, 102, 1)",
      },
    ],
  };

  const metricUnits = {
    moisture: "%",
    temperature: "°C",
    salinity: "dS/m",
  };

  const metricRanges = {
    moisture: { min: 0, max: 40 },
    temperature: { min: 15, max: 40 },
    salinity: { min: 0.1, max: 40 },
  };

  const defaultStepSizes = {
    moisture: 5,
    temperature: 5,
    salinity: 3.75,
  };

  // Fetch sites and user data on mount
  useEffect(() => {
    const fetchSitesAndUser = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Fetch sites
        const siteRes = await axios.get(`${baseURL}/get-sites/`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const siteList = siteRes.data.data || [];

        // Fetch user data
        const userRes = await axios.get(`${baseURL}/users/`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const userData = userRes.data[0] || {};
        const userSiteIds = userData.sites || [];

        // Filter user-assigned sites
        const userSites = siteList.filter((site) =>
          userSiteIds.includes(site.id)
        );

        // Fetch site details
        const siteDetailsPromises = userSites.map((site) =>
          axios.get(`${baseURL}/sites/${site.id}/?with_image=true`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          })
        );
        const siteDetailsRes = await Promise.all(siteDetailsPromises);
        const siteDetails = siteDetailsRes.map((res) => res.data);

        // Set initial site
        let initialSite = "";
        let initialGroup = "";
        let initialDevice = "";

        if (groupId) {
          const groupRes = await axios.get(`${baseURL}/groups/${groupId}`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          });
          const group = groupRes.data;
          if (group && userSiteIds.includes(group.site)) {
            initialGroup = group.id;
            initialSite = group.site;
          }
        } else if (deviceId) {
          const deviceRes = await axios.get(`${baseURL}/devices/${deviceId}`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          });
          const device = deviceRes.data;
          if (device && userSiteIds.includes(device.site)) {
            initialDevice = device.id;
            initialSite = device.site;
          }
        } else if (siteId && userSites.some((site) => site.id === siteId)) {
          initialSite = siteId;
        } else if (userSites.length > 0) {
          initialSite = userSites[0].id;
        }

        setSites(userSites);
        setSelectedSite(initialSite);
        setSelectedGroup(initialGroup);
        setSelectedDevice(initialDevice);
        setLastUpdated(new Date()); // Set timestamp after successful fetch
        setIsLoading(false);
      } catch (err) {
        setError("Failed to load sites. Please try again later.");
        setIsLoading(false);
      }
    };

    fetchSitesAndUser();
  }, [accessToken, groupId, deviceId, siteId]);

  // Fetch groups and devices when selectedSite changes
  useEffect(() => {
    const fetchGroupsAndDevices = async () => {
      if (!selectedSite) return;
      try {
        const [groupRes, deviceRes] = await Promise.all([
          axios.get(`${baseURL}/groups/?site=${selectedSite}`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          }),
          axios.get(`${baseURL}/devices/?site=${selectedSite}`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          }),
        ]);
        const groupList = groupRes.data || [];
        const deviceList = deviceRes.data || [];
        const filteredDevices = deviceList.filter(
          (device) =>
            device.device_type &&
            !["base", "echo"].includes(device.device_type.toLowerCase())
        );

        // Validate current selections
        setGroups(groupList);
        setDevices(filteredDevices);
        setSelectedGroup((prev) =>
          groupList.some((g) => g.id === prev) ? prev : ""
        );
        setSelectedDevice((prev) =>
          filteredDevices.some((d) => d.id === prev) ? prev : ""
        );
        setLastUpdated(new Date()); // Set timestamp after successful fetch
      } catch (err) {
        setError("Failed to load groups or devices.");
      }
    };

    fetchGroupsAndDevices();
  }, [accessToken, selectedSite]);

  // MAIN FILTER LOGIC — YOUR ORIGINAL, SIMPLE, WORKING VERSION
  useEffect(() => {
    const fetchMeasurements = async () => {
      if (!selectedSite) return;
      setIsLoading(true);
      try {
        let newMeasurements = { site: [], group: [], device: [] };
        let newDeviceDetails = null;

        let startDate, endDate;
        if (customStartDate && customEndDate) {
          startDate = new Date(customStartDate).toISOString();
          endDate = new Date(customEndDate).toISOString();
        } else {
          endDate = new Date().toISOString();
          const days = timeRange === "30d" ? 30 : timeRange === "14d" ? 14 : 7;
          startDate = new Date(
            new Date().setDate(new Date().getDate() - days)
          ).toISOString();
        }

        if (selectedDevice) {
          const [detailsRes, dataRes] = await Promise.all([
            axios.get(`${baseURL}/devices/${selectedDevice}`, {
              headers: { Authorization: `Bearer ${accessToken}` },
            }),
            axios.get(`${baseURL}/measurements/aggregated`, {
              headers: { Authorization: `Bearer ${accessToken}` },
              params: {
                device: selectedDevice,
                since: startDate,
                until: endDate,
                window_size: 500,
              },
            }),
          ]);
          newDeviceDetails = detailsRes.data;
          newMeasurements.device = dataRes.data || [];
        } else if (selectedGroup) {
          const group = groups.find((g) => g.id === selectedGroup);
          if (group?.devices?.length > 0) {
            const res = await axios.get(`${baseURL}/measurements/aggregated`, {
              headers: { Authorization: `Bearer ${accessToken}` },
              params: {
                device: group.devices.join(","),
                since: startDate,
                until: endDate,
                window_size: 125,
              },
            });
            newMeasurements.group = res.data || [];
          }
        } else {
          const siteDeviceIds = devices.map((d) => d.id).join(",");
          if (siteDeviceIds) {
            const res = await axios.get(`${baseURL}/measurements/aggregated`, {
              headers: { Authorization: `Bearer ${accessToken}` },
              params: {
                device: siteDeviceIds,
                since: startDate,
                until: endDate,
                window_size: 500,
                aggregate_all: true,
              },
            });
            newMeasurements.site = res.data || [];
          }
        }

        setMeasurements((prev) => ({ ...prev, ...newMeasurements })); // Preserve comparison data
        setDeviceDetails(newDeviceDetails);
        setLastUpdated(new Date());
        setIsLoading(false);
      } catch (err) {
        setError("Failed to load measurements.");
        setIsLoading(false);
      }
    };

    fetchMeasurements();
  }, [
    selectedSite,
    selectedGroup,
    selectedDevice,
    timeRange,
    customStartDate,
    customEndDate,
    groups,
    devices,
  ]);

  // Reset comparison data when time range changes to prevent stale data display
  useEffect(() => {
    if (compareMode && selectedForComparison.length > 0) {
      setIsComparisonLoading(true);
      setMeasurements((prev) => ({ ...prev, comparison: [] }));
    }
  }, [timeRange, customStartDate, customEndDate]);

  useEffect(() => {
    if (!compareMode || selectedForComparison.length === 0) {
      setMeasurements((prev) => ({ ...prev, comparison: [] }));
      return;
    }
    const fetchComparisonData = async () => {
      setIsComparisonLoading(true);
      try {
        let startDate, endDate;
        if (customStartDate && customEndDate) {
          startDate = new Date(customStartDate).toISOString();
          endDate = new Date(customEndDate).toISOString();
        } else {
          const days = timeRange === "30d" ? 30 : timeRange === "14d" ? 14 : 7;
          const end = new Date();
          const start = new Date(end);
          start.setDate(end.getDate() - days);
          endDate = end.toISOString();
          startDate = start.toISOString();
        }

        let comparisonData = [];

        if (compareByGroup) {
          // Fetch EACH GROUP separately → get accurate average per group
          const groupPromises = selectedForComparison.map((groupId) => {
            const group = groups.find((g) => g.id === groupId);
            if (!group || !group.devices?.length) return null;

            return axios
              .get(`${baseURL}/measurements/aggregated`, {
                headers: { Authorization: `Bearer ${accessToken}` },
                params: {
                  device: group.devices.join(","),
                  since: startDate,
                  until: endDate,
                  window_size: 200,
                  aggregate_all: true, // now safe — only one group
                },
              })
              .then((res) => ({
                groupId,
                groupName: group.name || `Group ${groupId}`,
                data: res.data || [],
              }));
          });

          const results = await Promise.all(groupPromises);
          comparisonData = results.filter(Boolean);
        } else {
          // Device comparison: fetch all at once (no aggregate_all)
          const res = await axios.get(`${baseURL}/measurements/aggregated`, {
            headers: { Authorization: `Bearer ${accessToken}` },
            params: {
              device: selectedForComparison.join(","),
              since: startDate,
              until: endDate,
              window_size: 200,
            },
          });
          comparisonData = res.data || [];
        }
        setMeasurements((prev) => ({ ...prev, comparison: comparisonData }));
        setLastUpdated(new Date());
        setIsComparisonLoading(false);
      } catch (err) {
        console.error(err);
        setError("Failed to load comparison data");
        setIsComparisonLoading(false);
      }
    };

    fetchComparisonData();
  }, [
    selectedForComparison,
    timeRange,
    customStartDate,
    customEndDate,
    compareMode,
    compareByGroup,
    groups,
    accessToken,
  ]);

  // Only reset compare mode when user manually switches filters
  // Do NOT reset when page loads and selectedSite is being set
  useEffect(() => {
    // Only run when user actually changes selection (not on initial mount)
    if (selectedSite && (selectedGroup || selectedDevice)) {
      if (compareMode) {
        setCompareMode(false);
        setSelectedForComparison([]);
        setCompareByGroup(false);
      }
    }
  }, [selectedGroup, selectedDevice]); // ← Only watch group/device changes

  // Handle selection changes
  const handleSiteChange = (event) => {
    setSelectedSite(event.target.value);
    setSelectedGroup("");
    setSelectedDevice("");
  };

  const handleGroupChange = (event) => {
    setSelectedGroup(event.target.value);
    setSelectedDevice("");
  };

  const handleDeviceChange = (event) => {
    setSelectedDevice(event.target.value);
    setSelectedGroup("");
  };

  const handleTimeRangeChange = (event, newValue) => {
    if (newValue) {
      setTimeRange(newValue);
      setCustomStartDate(null);
      setCustomEndDate(null);
    }
  };

  const handleResetDateRange = () => {
    setTimeRange("7d");
    setCustomStartDate(null);
    setCustomEndDate(null);
  };

  // Prepare chart data
  const getChartData = (rawData, metric, type, groupDevices = []) => {
    const isGroupCompare = type === "Comparison" && compareByGroup;
    const isDeviceCompare = type === "Comparison" && !compareByGroup;

    if (!rawData) {
      return { labels: [], datasets: [] };
    }

    if (
      !rawData ||
      (Array.isArray(rawData)
        ? rawData.length === 0
        : Object.keys(rawData).length === 0)
    ) {
      return { labels: [], datasets: [] };
    }

    // GROUP COMPARISON: rawData is array of { groupId, groupName, data: [...] }
    if (isGroupCompare) {
      if (
        !Array.isArray(rawData) ||
        rawData.length === 0 ||
        !rawData[0]?.data
      ) {
        return { labels: [], datasets: [] };
      }
      // Find the longest timeline to align all groups
      const allTimestamps = new Set();
      rawData.forEach((item) => {
        item.data.forEach((d) => allTimestamps.add(d.timestamp.median));
      });
      const sortedTimestamps = Array.from(allTimestamps).sort();

      const labels = sortedTimestamps.map((ts) => {
        const date = new Date(ts);
        return date.toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
          ...(timeRange === "30d" && !customStartDate
            ? {}
            : { weekday: isXsScreen ? undefined : "short" }),
        });
      });

      const datasets = rawData.map((groupData, index) => {
        const values = sortedTimestamps.map((ts) => {
          const point = groupData.data.find((d) => d.timestamp.median === ts);
          const val = point?.[metric]?.mean;
          return val != null ? (metric === "moisture" ? val * 100 : val) : null;
        });

        return {
          label: groupData.groupName,
          data: values,
          borderColor: metricColors[metric][index % 4].borderColor,
          backgroundColor: metricColors[metric][index % 4].backgroundColor,
          pointBackgroundColor:
            metricColors[metric][index % 4].pointBackgroundColor,
          borderWidth: 2,
          tension: 0.5,
          fill: false,
          spanGaps: true,
          pointRadius: 0,
          pointHoverRadius: 6,
        };
      });

      return { labels, datasets };
    }

    // DEVICE COMPARISON & SINGLE VIEW: rawData is array of measurements
    const data = Array.isArray(rawData) ? rawData : rawData.data || [];

    if (data.length === 0) return { labels: [], datasets: [] };

    const labels = data.map((item) => {
      const date = new Date(item.timestamp.median);
      return date.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        ...(timeRange === "30d" && !customStartDate
          ? {}
          : { weekday: isXsScreen ? undefined : "short" }),
      });
    });

    if (isDeviceCompare || type === "Group") {
      const deviceList =
        type === "Group" ? groupDevices : selectedForComparison;
      const datasets = deviceList.map((deviceId, index) => {
        const deviceName =
          devices.find((d) => d.id === deviceId)?.name || `Device ${deviceId}`;
        const values = data.map((item) => {
          if (item.devices?.includes(deviceId)) {
            const val = item[metric]?.mean;
            return val != null
              ? metric === "moisture"
                ? val * 100
                : val
              : null;
          }
          return null;
        });

        return {
          label: deviceName,
          data: values,
          borderColor: metricColors[metric][index % 4].borderColor,
          backgroundColor: metricColors[metric][index % 4].backgroundColor,
          borderWidth: 2.5,
          tension: 0.5,
          fill: false,
          spanGaps: true,
          pointRadius: 0,
        };
      });
      return { labels, datasets };
    }

    // Single view
    const values = data.map((item) => {
      const val = item[metric]?.mean;
      return val != null ? (metric === "moisture" ? val * 100 : val) : null;
    });

    return {
      labels,
      datasets: [
        {
          label: metric.charAt(0).toUpperCase() + metric.slice(1),
          data: values,
          borderColor: metricColors[metric][0].borderColor,
          backgroundColor: metricColors[metric][0].backgroundColor,
          borderWidth: 2,
          tension: 0.5,
          fill: false,
          spanGaps: true,
          pointRadius: 0,
          pointHoverRadius: 6,
        },
      ],
    };
  };

  // Calculate y-axis bounds and step size
  const calculateChartBounds = (data, metric, type, deviceList = []) => {
    let values = [];

    if (type === "Group" || type === "Comparison") {
      if (!data || deviceList.length === 0) return metricRanges[metric];
      deviceList.forEach((deviceId) => {
        data.forEach((item) => {
          if (item.devices?.includes(deviceId)) {
            const val = item[metric]?.mean;
            if (val != null) {
              values.push(metric === "moisture" ? val * 100 : val);
            }
          }
        });
      });
    } else {
      // single device/site logic
      values = data
        .map((item) => {
          const val = item[metric]?.mean;
          return val != null ? (metric === "moisture" ? val * 100 : val) : null;
        })
        .filter((v) => v !== null);
    }

    if (values.length === 0) return metricRanges[metric];
    const min = Math.floor(Math.min(...values) / 5) * 5;
    const max = Math.ceil(Math.max(...values) / 5) * 5;
    return { min, max, stepSize: (max - min) / 8 };
  };

  // Configure Chart.js options
  const getChartOptions = (metric, type, data, groupDevices = []) => {
    const fontSizeBase = isXsScreen ? 7 : 10;
    const maxTicksLimit = isXsScreen ? 4 : 7;

    const bounds = calculateChartBounds(data, metric, type, groupDevices) || {
      min: metricRanges[metric].min,
      max: metricRanges[metric].max,
      stepSize: defaultStepSizes[metric],
    };

    return {
      responsive: true,
      maintainAspectRatio: false,
      cubicInterpolationMode: "monotone",
      scales: {
        x: {
          title: {
            display: !isXsScreen,
            text: "Date",
            font: { size: fontSizeBase + 2 },
          },
          grid: { display: false },
          ticks: {
            maxRotation: isXsScreen ? 90 : 30,
            minRotation: 0,
            font: { size: fontSizeBase },
            autoSkip: true,
            maxTicksLimit: maxTicksLimit,
          },
        },
        y: {
          title: {
            display: !isXsScreen,
            text: `${metric.charAt(0).toUpperCase() + metric.slice(1)} (${
              metricUnits[metric]
            })`,
            font: { size: fontSizeBase + 2 },
          },
          min: bounds.min,
          max: bounds.max,
          ticks: {
            stepSize: bounds.stepSize,
            font: { size: fontSizeBase },
          },
        },
      },
      plugins: {
        legend: {
          display: type === "Group" || type === "Comparison",
          labels: {
            font: { size: fontSizeBase + 1 },
          },
        },
        tooltip: {
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          titleFont: { size: fontSizeBase + 4 },
          bodyFont: { size: fontSizeBase + 3 },
          padding: isXsScreen ? 6 : 10,
          cornerRadius: 4,
          callbacks: {
            label: (context) => {
              const value = context.parsed.y
                ? context.parsed.y.toFixed(1)
                : "N/A";
              return `${context.dataset.label}: ${value} ${metricUnits[metric]}`;
            },
          },
        },
      },
      interaction: { mode: "index", intersect: false },
      elements: { line: { borderJoinStyle: "round" } },
    };
  };

  // Display measurements and charts
  const MeasurementSection = ({
    type,
    id,
    name,
    data,
    lastMeasurement,
    groupDevices = [],
  }) => {
    // Check loading state first for comparison mode
    if (type === "Comparison" && isComparisonLoading) {
      return (
        <Box sx={{ py: 8, textAlign: "center" }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Loading comparison data...
          </Typography>
          <LinearProgress sx={{ mt: 2, mx: "20%", borderRadius: 2 }} />
        </Box>
      );
    }

    // Check data availability - only show "Select items" if nothing is selected
    // For group comparison, data is [{groupId, groupName, data: [...]}]
    // For device comparison, data is [...]
    const hasData = (() => {
      if (!data) return false;
      if (!Array.isArray(data)) return false;
      if (data.length === 0) return false;

      // For group comparison, check if any group has actual measurement data
      if (type === "Comparison" && compareByGroup) {
        return data.some(
          (item) =>
            item &&
            item.data &&
            Array.isArray(item.data) &&
            item.data.length > 0
        );
      }

      // For device comparison or regular views, data is a direct array
      return true;
    })();

    if (!hasData) {
      return (
        <Box sx={{ py: 8, textAlign: "center" }}>
          <Typography color="text.secondary">
            {type === "Comparison" && selectedForComparison.length === 0
              ? "Select items to compare"
              : type === "Comparison"
              ? "No data available for selected items"
              : "No data available"}
          </Typography>
        </Box>
      );
    }

    const calculateMetricBounds = (data, metric) => {
      let values = [];
      if (type === "Group") {
        groupDevices.forEach((deviceId) => {
          data.forEach((item) => {
            if (item.devices && item.devices.includes(deviceId)) {
              const value = item[metric]?.mean || 0;
              values.push(metric === "moisture" ? value * 100 : value);
            }
          });
        });
      } else {
        values = data.map((item) => {
          const value = item[metric]?.mean || 0;
          return metric === "moisture" ? value * 100 : value;
        });
      }
      values = values.filter((v) => v !== 0);
      if (values.length === 0) return { min: 0, max: 100 };
      return {
        min: Math.floor(Math.min(...values)),
        max: Math.ceil(Math.max(...values)),
      };
    };

    const getMetricThresholds = (data) => ({
      moisture: {
        critical: calculateMetricBounds(data, "moisture"),
        optimal: { min: 20, max: 80 },
        unit: "%",
        formatValue: (value) => (value * 100).toFixed(1),
        getMessage: (value) => {
          const percent = value * 100;
          if (percent < 20) return "Too Dry";
          if (percent > 80) return "Too Wet";
          return "Optimal";
        },
      },
      temperature: {
        critical: calculateMetricBounds(data, "temperature"),
        optimal: { min: 20, max: 30 },
        unit: "°C",
        formatValue: (value) => value.toFixed(1),
        getMessage: (value) => {
          if (value < 20) return "Too Cold";
          if (value > 30) return "Too Hot";
          return "Optimal";
        },
      },
      salinity: {
        critical: calculateMetricBounds(data, "salinity"),
        optimal: { min: 2, max: 8 },
        unit: "dS/m",
        formatValue: (value) => value.toFixed(1),
        getMessage: (value) => {
          if (value < 2) return "Low Salt";
          if (value > 8) return "High Salt";
          return "Optimal";
        },
      },
    });

    const getStatusColor = (value, metric, thresholds) => {
      const numValue = metric === "moisture" ? value * 100 : value;
      if (!value) return "grey.500";
      if (
        numValue < thresholds.optimal.min ||
        numValue > thresholds.optimal.max
      )
        return "error.main";
      return "success.main";
    };

    if (!data || data.length === 0) {
      return <LoadingSkeleton />;
    }

    const metricThresholds = getMetricThresholds(data);

    return (
      <Box>
        {type === "Device" && lastMeasurement && (
          <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 4 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ fontSize: { xs: "1.1rem", md: "1.25rem" }, mb: 3 }}
            >
              Current Readings
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {Object.entries(metricThresholds).map(([metric, config]) => {
                const value = lastMeasurement?.[metric] || 0;
                const displayValue =
                  metric === "moisture" ? value * 100 : value;
                const bounds = config.critical;
                const normalizedValue =
                  ((displayValue - bounds.min) / (bounds.max - bounds.min)) *
                  100;
                const statusColor = getStatusColor(value, metric, config);
                const clampedValue = Math.min(
                  100,
                  Math.max(0, normalizedValue)
                );

                if (normalizedValue <= 0) {
                  return (
                    <Box key={metric}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 500,
                            fontSize: { xs: "0.9rem", sm: "1rem" },
                          }}
                        >
                          {metric.charAt(0).toUpperCase() + metric.slice(1)}
                        </Typography>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            color: "error.main",
                            fontWeight: 500,
                            fontSize: { xs: "0.9rem", sm: "1rem" },
                          }}
                        >
                          {`Below Range of ${config.formatValue(value)} ${
                            config.unit
                          }`}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={0}
                        sx={{
                          height: 10,
                          borderRadius: 5,
                          backgroundColor: "grey.200",
                          "& .MuiLinearProgress-bar": {
                            backgroundColor: "error.main",
                            borderRadius: 5,
                          },
                        }}
                      />
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mt: 0.5,
                          px: 0.5,
                        }}
                      >
                        <Typography variant="caption" color="text.primary">
                          {bounds.min} {config.unit}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "success.main",
                            fontSize: { xs: "0.7rem", sm: "0.75rem" },
                          }}
                        >
                          Optimal: {config.optimal.min}-{config.optimal.max}{" "}
                          {config.unit}
                        </Typography>
                        <Typography variant="caption" color="text.primary">
                          {bounds.max} {config.unit}
                        </Typography>
                      </Box>
                    </Box>
                  );
                }

                return (
                  <Box key={metric}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 500,
                          fontSize: { xs: "0.9rem", sm: "1rem" },
                        }}
                      >
                        {metric.charAt(0).toUpperCase() + metric.slice(1)}
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          color: statusColor,
                          fontWeight: 500,
                          fontSize: { xs: "0.9rem", sm: "1rem" },
                        }}
                      >
                        {config.formatValue(value)} {config.unit}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={clampedValue}
                      sx={{
                        height: 10,
                        borderRadius: 5,
                        backgroundColor: "grey.200",
                        "& .MuiLinearProgress-bar": {
                          backgroundColor: statusColor,
                          borderRadius: 5,
                        },
                      }}
                    />
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mt: 0.5,
                        px: 0.5,
                      }}
                    >
                      <Typography variant="caption" color="text.primary">
                        {bounds.min} {config.unit}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "success.main",
                          fontSize: { xs: "0.7rem", sm: "0.75rem" },
                        }}
                      >
                        Optimal: {config.optimal.min}-{config.optimal.max}{" "}
                        {config.unit}
                      </Typography>
                      <Typography variant="caption" color="text.primary">
                        {bounds.max} {config.unit}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Paper>
        )}
        <Box sx={{ mt: { xs: 2, sm: 3 } }}>
          {["moisture", "temperature", "salinity"].map((metric) => (
            <Paper
              key={`${metric}-chart`}
              sx={{
                p: { xs: 1.5, sm: 2 },
                mb: 3,
                borderRadius: 2,
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              }}
            >
              <Typography
                variant="h6"
                fontWeight="500"
                color={metricColors[metric][0].borderColor}
                sx={{
                  mb: { xs: 1, sm: 2 },
                  fontSize: { xs: "0.95rem", sm: "1.1rem", md: "1.25rem" },
                }}
              >
                {metric.charAt(0).toUpperCase() + metric.slice(1)} Over Time
              </Typography>
              <Box sx={{ height: { xs: 200, sm: 300 }, width: "100%" }}>
                <Line
                  data={getChartData(data, metric, type, groupDevices)}
                  options={getChartOptions(metric, type, data, groupDevices)}
                />
              </Box>
            </Paper>
          ))}
        </Box>
      </Box>
    );
  };

  // Skeleton UI for loading
  const LoadingSkeleton = () => (
    <Box sx={{ p: 0 }}>
      <Skeleton variant="text" width="30%" height={40} />
      <Skeleton variant="rectangular" width="100%" height={56} sx={{ mb: 3 }} />
      <Skeleton variant="rectangular" width="100%" height={56} sx={{ mb: 3 }} />
      <Skeleton variant="rectangular" width="100%" height={600} />
    </Box>
  );

  // Render
  if (isLoading) return <LoadingSkeleton />;
  if (error)
    return (
      <Typography color="error" sx={{ p: 3 }}>
        {error}
      </Typography>
    );
  if (sites.length === 0)
    return <Typography sx={{ p: 3 }}>No sites available.</Typography>;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3 } }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{ fontSize: { xs: "1.75rem", md: "2.125rem" } }}
        >
          Statistics
        </Typography>

        {/* Selectors */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            mb: 3,
            alignItems: { sm: "center" },
          }}
        >
          <FormControl
            variant="outlined"
            sx={{ minWidth: { xs: "100%", sm: 200 } }}
          >
            <InputLabel id="site-select-label">Site</InputLabel>
            <Select
              labelId="site-select-label"
              value={selectedSite}
              onChange={handleSiteChange}
              label="Site"
            >
              {sites.map((site) => (
                <MenuItem key={site.id} value={site.id}>
                  {site.name || `Site ${site.id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl
            variant="outlined"
            sx={{ minWidth: { xs: "100%", sm: 200 } }}
          >
            <InputLabel id="group-select-label">Group</InputLabel>
            <Select
              labelId="group-select-label"
              value={selectedGroup}
              onChange={handleGroupChange}
              label="Group"
            >
              <MenuItem value="">None</MenuItem>
              {groups.map((group) => (
                <MenuItem key={group.id} value={group.id}>
                  {group.name || `Group ${group.id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl
            variant="outlined"
            sx={{ minWidth: { xs: "100%", sm: 200 } }}
          >
            <InputLabel id="device-select-label">Device</InputLabel>
            <Select
              labelId="device-select-label"
              value={selectedDevice}
              onChange={handleDeviceChange}
              label="Device"
            >
              <MenuItem value="">None</MenuItem>
              {devices.map((device) => (
                <MenuItem key={device.id} value={device.id}>
                  {device.name || `Device ${device.id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              opacity: 0.7,
              mt: { xs: 1, sm: 0 },
              alignSelf: { sm: "center" },
            }}
          >
            {isLoading
              ? "Updating..."
              : lastUpdated
              ? `Last updated: ${lastUpdated.toLocaleString()}`
              : "No updates yet"}
          </Typography>
        </Box>


        {/* Date Range Controls */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            mb: 2,
            alignItems: { sm: "center" },
          }}
        >
          <ToggleButtonGroup
            value={timeRange}
            exclusive
            onChange={handleTimeRangeChange}
            size={isXsScreen ? "small" : "medium"}
            disabled={customStartDate || customEndDate}
          >
            <ToggleButton value="7d">7 Days</ToggleButton>
            <ToggleButton value="14d">14 Days</ToggleButton>
            <ToggleButton value="30d">30 Days</ToggleButton>
          </ToggleButtonGroup>
          <DatePicker
            label="Start Date"
            value={customStartDate}
            onChange={(newValue) => {
              setCustomStartDate(newValue);
              setTimeRange("");
            }}
            maxDate={customEndDate || new Date()}
            renderInput={(params) => (
              <TextField {...params} sx={{ width: { xs: "100%", sm: 150 } }} />
            )}
          />
          <DatePicker
            label="End Date"
            value={customEndDate}
            onChange={(newValue) => {
              setCustomEndDate(newValue);
              setTimeRange("");
            }}
            minDate={customStartDate}
            maxDate={new Date()}
            renderInput={(params) => (
              <TextField {...params} sx={{ width: { xs: "100%", sm: 150 } }} />
            )}
          />
          {(customStartDate || customEndDate) && (
            <Button
              variant="outlined"
              size={isXsScreen ? "small" : "medium"}
              onClick={handleResetDateRange}
            >
              Reset
            </Button>
          )}
        </Box>

        <ToggleButtonGroup
          value={compareMode ? "compare" : "single"}
          sx={{
            mb: 2,
          }}
          exclusive
          onChange={(e, value) => {
            if (!value) return; // prevents error when clicking already selected

            const isCompare = value === "compare";
            setCompareMode(isCompare);

            if (!isCompare) {
              setSelectedForComparison([]);
              setCompareByGroup(false);
              setSelectedDevice("");
              setSelectedGroup("");
            }
          }}
          size={isXsScreen ? "small" : "medium"}
        >
          <ToggleButton value="single">Single View</ToggleButton>
          <ToggleButton value="compare">Compare View</ToggleButton>
        </ToggleButtonGroup>
                <Box>
          <Button
            variant="contained"
            color="secondary"
            onClick={() =>
              navigate("/water-balance", {
                state: selectedSite ? { siteId: selectedSite } : undefined,
              })
            }
            sx={{
              fontSize: { xs: "0.9rem", md: "1rem" },
              textTransform: "none",
              color: "white",
            }}
          >
            Water Saved Data
          </Button>
        </Box>

        {/* Autocomplete only shows in compare mode */}
        {compareMode && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Compare by:
            </Typography>
            <ToggleButtonGroup
              value={compareByGroup ? "group" : "device"}
              exclusive
              onChange={(_, v) => {
                if (v) {
                  setCompareByGroup(v === "group");
                  setSelectedForComparison([]);
                }
              }}
              size="small"
              sx={{ mb: 2 }}
            >
              <ToggleButton value="device">Devices</ToggleButton>
              <ToggleButton value="group">Groups</ToggleButton>
            </ToggleButtonGroup>

            {compareByGroup ? (
              <Autocomplete
                multiple
                options={groups}
                getOptionLabel={(g) => g.name || `Group ${g.id}`}
                value={groups.filter((g) =>
                  selectedForComparison.includes(g.id)
                )}
                onChange={(_, newValue) => {
                  const groupIds = newValue.map((g) => g.id || []);
                  setSelectedForComparison(groupIds);
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Select Groups to Compare" />
                )}
                sx={{ maxWidth: 600 }}
              />
            ) : (
              <Autocomplete
                multiple
                options={devices}
                getOptionLabel={(d) => d.name || `Device ${d.id}`}
                value={devices.filter((d) =>
                  selectedForComparison.includes(d.id)
                )}
                onChange={(_, newValue) => {
                  setSelectedForComparison(newValue.map((d) => d.id));
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Select Devices to Compare" />
                )}
                sx={{ maxWidth: 600 }}
              />
            )}
          </Box>
        )}

        <>
          {/* Content Area */}
          <Paper sx={{ p: { xs: 2, sm: 3 } }}>
            {compareMode && selectedForComparison.length > 0 ? (
              <MeasurementSection
                type="Comparison"
                data={measurements.comparison}
              />
            ) : selectedDevice ? (
              <MeasurementSection
                type="Device"
                id={selectedDevice}
                name={
                  devices.find((d) => d.id === selectedDevice)?.name ||
                  `Device ${selectedDevice}`
                }
                data={measurements.device}
                lastMeasurement={deviceDetails?.last_measurement}
              />
            ) : selectedGroup ? (
              <MeasurementSection
                type="Group"
                id={selectedGroup}
                name={
                  groups.find((g) => g.id === selectedGroup)?.name ||
                  `Group ${selectedGroup}`
                }
                data={measurements.group}
                groupDevices={
                  groups.find((g) => g.id === selectedGroup)?.devices || []
                }
              />
            ) : (
              <MeasurementSection
                type="Site"
                id={selectedSite}
                name={
                  sites.find((s) => s.id === selectedSite)?.name ||
                  `Site ${selectedSite}`
                }
                data={measurements.site}
              />
            )}
          </Paper>
        </>
      </Container>
    </LocalizationProvider>
  );
};

export default Statistics;
